import styled from "styled-components";

import { RestoreIcon, SaveIcon, TrashIcon } from "../../icons/glyphs";
import { IconButton } from "../IconButton";
import { Modal } from "../Modal";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants } from "../Text";
import { annotationStudioText } from "./messages";
import type { AnnotationStudioLocale } from "./types";

export interface StudioDialogsProps {
  readonly locale: AnnotationStudioLocale;
  readonly clearAllOpen: boolean;
  readonly removeFrameOpen: boolean;
  readonly removingFrame: boolean;
  readonly discardChangesOpen: boolean;
  readonly draftHasSavedBase: boolean;
  readonly cameraChangeOpen: boolean;
  readonly cameraChangeSaving: boolean;
  readonly cameraChangeError: string | null;
  readonly discardAndChangeCameraDisabled: boolean;
  readonly saveAndChangeCameraDisabled: boolean;
  readonly leaveConfirmationOpen: boolean;
  readonly onCloseClearAll: () => void;
  readonly onConfirmClearAll: () => void;
  readonly onCloseRemoveFrame: () => void;
  readonly onConfirmRemoveFrame: () => void;
  readonly onCloseDiscardChanges: () => void;
  readonly onConfirmDiscardChanges: () => void;
  readonly onCloseCameraChange: () => void;
  readonly onDiscardAndChangeCamera: () => void;
  readonly onSaveAndChangeCamera: () => void;
  readonly onCancelLeave: () => void;
  readonly onConfirmLeave: () => void;
}

const ModalErrorMessage = styled(Text)`
  margin-top: 0.65rem;
  color: ${({ theme }) => theme.color.text.danger};
`;

export function StudioDialogs({
  locale,
  clearAllOpen,
  removeFrameOpen,
  removingFrame,
  discardChangesOpen,
  draftHasSavedBase,
  cameraChangeOpen,
  cameraChangeSaving,
  cameraChangeError,
  discardAndChangeCameraDisabled,
  saveAndChangeCameraDisabled,
  leaveConfirmationOpen,
  onCloseClearAll,
  onConfirmClearAll,
  onCloseRemoveFrame,
  onConfirmRemoveFrame,
  onCloseDiscardChanges,
  onConfirmDiscardChanges,
  onCloseCameraChange,
  onDiscardAndChangeCamera,
  onSaveAndChangeCamera,
  onCancelLeave,
  onConfirmLeave,
}: StudioDialogsProps) {
  return (
    <>
      <Modal open={clearAllOpen} onClose={onCloseClearAll} size="sm">
        <Modal.Header divider={false}>
          <Modal.Header.Start>
            <Modal.Header.Title>
              {annotationStudioText("clearAllTargetsTitle", locale)}
            </Modal.Header.Title>
          </Modal.Header.Start>
        </Modal.Header>
        <Modal.Body scrollable={false}>
          <Text variant={defaultTextVariants["body-normal"]}>
            {annotationStudioText("clearAllTargetsDescription", locale)}
          </Text>
        </Modal.Body>
        <Modal.Footer divider={false}>
          <IconButton
            text={annotationStudioText("cancel", locale)}
            variant="surface"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- destructive confirm의 safe default: 열리자마자 Enter는 취소
            autoFocus
            onClick={onCloseClearAll}
          />
          <IconButton
            text={annotationStudioText("clearAllTargetsConfirm", locale)}
            variant="solid"
            tone="danger"
            onClick={onConfirmClearAll}
          >
            <TrashIcon />
          </IconButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={removeFrameOpen}
        onClose={onCloseRemoveFrame}
        closeOnEscape={!removingFrame}
        closeOnBackdropClick={!removingFrame}
        size="sm"
      >
        <Modal.Header divider={false}>
          <Modal.Header.Start>
            <Modal.Header.Title>
              {annotationStudioText("removeCapturedFrameTitle", locale)}
            </Modal.Header.Title>
          </Modal.Header.Start>
        </Modal.Header>
        <Modal.Body scrollable={false}>
          <Text variant={defaultTextVariants["body-normal"]}>
            {annotationStudioText("removeCapturedFrameDescription", locale)}
          </Text>
        </Modal.Body>
        <Modal.Footer divider={false}>
          <IconButton
            text={annotationStudioText("cancel", locale)}
            variant="surface"
            disabled={removingFrame}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- destructive confirm의 safe default: 열리자마자 Enter는 취소
            autoFocus
            onClick={onCloseRemoveFrame}
          />
          <IconButton
            text={annotationStudioText(
              removingFrame ? "removingCapturedFrame" : "removeCapturedFrame",
              locale,
            )}
            variant="solid"
            tone="danger"
            disabled={removingFrame}
            onClick={onConfirmRemoveFrame}
          >
            {removingFrame ? (
              <Spinner label={annotationStudioText("removingCapturedFrame", locale)} size="sm" />
            ) : (
              <TrashIcon />
            )}
          </IconButton>
        </Modal.Footer>
      </Modal>

      <Modal open={discardChangesOpen} onClose={onCloseDiscardChanges} size="sm">
        <Modal.Header divider={false}>
          <Modal.Header.Start>
            <Modal.Header.Title>
              {annotationStudioText("discardChangesTitle", locale)}
            </Modal.Header.Title>
          </Modal.Header.Start>
        </Modal.Header>
        <Modal.Body scrollable={false}>
          <Text variant={defaultTextVariants["body-normal"]}>
            {annotationStudioText(
              draftHasSavedBase ? "discardSavedChangesDescription" : "discardNewDraftDescription",
              locale,
            )}
          </Text>
        </Modal.Body>
        <Modal.Footer divider={false}>
          <IconButton
            text={annotationStudioText("cancel", locale)}
            variant="surface"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- destructive confirm의 safe default: 열리자마자 Enter는 취소
            autoFocus
            onClick={onCloseDiscardChanges}
          />
          <IconButton
            text={annotationStudioText("discardChanges", locale)}
            variant="solid"
            tone="danger"
            onClick={onConfirmDiscardChanges}
          >
            <RestoreIcon />
          </IconButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={cameraChangeOpen}
        onClose={onCloseCameraChange}
        closeOnEscape={!cameraChangeSaving}
        closeOnBackdropClick={!cameraChangeSaving}
        size="sm"
      >
        <Modal.Header divider={false}>
          <Modal.Header.Start>
            <Modal.Header.Title>
              {annotationStudioText("changeCameraTitle", locale)}
            </Modal.Header.Title>
          </Modal.Header.Start>
        </Modal.Header>
        <Modal.Body scrollable={false}>
          <Text variant={defaultTextVariants["body-normal"]}>
            {annotationStudioText("changeCameraUnsavedDescription", locale)}
          </Text>
          {cameraChangeError ? (
            <ModalErrorMessage role="alert" variant={defaultTextVariants["body-normal"]}>
              {cameraChangeError}
            </ModalErrorMessage>
          ) : null}
        </Modal.Body>
        <Modal.Footer divider={false}>
          <IconButton
            text={annotationStudioText("continueEditing", locale)}
            variant="surface"
            disabled={cameraChangeSaving}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- destructive confirm의 safe default: 열리자마자 Enter는 계속 편집
            autoFocus
            onClick={onCloseCameraChange}
          />
          <IconButton
            text={annotationStudioText("discardAndChangeCamera", locale)}
            variant="surface"
            tone="danger"
            disabled={discardAndChangeCameraDisabled}
            onClick={onDiscardAndChangeCamera}
          />
          <IconButton
            text={annotationStudioText(
              cameraChangeSaving ? "savingAndChangeCamera" : "saveAndChangeCamera",
              locale,
            )}
            variant="solid"
            disabled={saveAndChangeCameraDisabled}
            onClick={onSaveAndChangeCamera}
          >
            {cameraChangeSaving ? (
              <Spinner label={annotationStudioText("savingAndChangeCamera", locale)} size="sm" />
            ) : (
              <SaveIcon />
            )}
          </IconButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={leaveConfirmationOpen}
        onClose={onCancelLeave}
        closeOnEscape={false}
        closeOnBackdropClick={false}
        size="sm"
      >
        <Modal.Header divider={false}>
          <Modal.Header.Start>
            <Modal.Header.Title>
              {annotationStudioText("unsavedChangesTitle", locale)}
            </Modal.Header.Title>
          </Modal.Header.Start>
        </Modal.Header>
        <Modal.Body scrollable={false}>
          <Text variant={defaultTextVariants["body-normal"]}>
            {annotationStudioText("unsavedChangesDescription", locale)}
          </Text>
        </Modal.Body>
        <Modal.Footer divider={false}>
          <IconButton
            text={annotationStudioText("continueEditing", locale)}
            variant="surface"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- destructive leave에서 안전한 취소 action을 초기 focus로 둔다.
            autoFocus
            onClick={onCancelLeave}
          />
          <IconButton
            text={annotationStudioText("leaveWithoutSaving", locale)}
            variant="solid"
            tone="danger"
            onClick={onConfirmLeave}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
}
